import { useEffect, type ComponentType, type CSSProperties, type ReactNode, type SVGProps } from "react";
import type { VoiceParticipant } from "@atrium/shared";
import { cn } from "../../lib/cn";
import { Avatar } from "../../components/ui/Avatar";
import { IconButton } from "../../components/ui/Button";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuLabel, ContextMenuTrigger } from "../../components/ui/ContextMenu";
import { Menu, MenuContent, MenuItem, MenuLabel, MenuTrigger } from "../../components/ui/Menu";
import { Tooltip } from "../../components/ui/Tooltip";
import { IconDeafened, IconMicOff, IconMore } from "../../components/icons";
import { useVoice } from "./VoiceProvider";
import { useStreamUi } from "./stores";
import { LiveBadge, StreamTile } from "./StreamTile";

export { LiveBadge, StreamVideo } from "./StreamTile";

export interface ParticipantAction {
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  danger?: boolean;
  run: () => void;
}

export function MuteIcon({ p, className = "size-4" }: { p: VoiceParticipant; className?: string }) {
  if (p.deafened) return <IconDeafened className={cn("icon text-danger", className)} aria-label="Deafened" />;
  if (p.serverMuted && p.speaker !== false)
    return (
      <Tooltip label="Muted by a moderator">
        <IconMicOff className={cn("icon text-danger", className)} aria-label="Muted by a moderator" />
      </Tooltip>
    );
  if (p.muted && p.speaker !== false) return <IconMicOff className={cn("icon text-fg-3", className)} aria-label="Muted" />;
  return null;
}

/** Right-click menu plus a "⋯" button (for keyboard and touch) with the same actions. */
export function WithActions({ p, actions, children, buttonClassName }: { p: VoiceParticipant; actions: ParticipantAction[]; children: ReactNode; buttonClassName?: string }) {
  if (!actions.length) return <>{children}</>;
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuLabel>{p.user.displayName}</ContextMenuLabel>
        {actions.map((a) => (
          <ContextMenuItem key={a.label} icon={a.icon} danger={a.danger} onSelect={a.run}>
            {a.label}
          </ContextMenuItem>
        ))}
      </ContextMenuContent>
    </ContextMenu>
  );
}

export function ActionsButton({ p, actions, className }: { p: VoiceParticipant; actions: ParticipantAction[]; className?: string }) {
  if (!actions.length) return null;
  return (
    <Menu>
      <MenuTrigger asChild>
        <IconButton size="sm" label={`Options for ${p.user.displayName}`} icon={IconMore} className={className} />
      </MenuTrigger>
      <MenuContent>
        <MenuLabel>{p.user.displayName}</MenuLabel>
        {actions.map((a) => (
          <MenuItem key={a.label} icon={a.icon} danger={a.danger} onSelect={a.run}>
            {a.label}
          </MenuItem>
        ))}
      </MenuContent>
    </Menu>
  );
}

function PersonTile({ p, actions, compact, className }: { p: VoiceParticipant; actions: ParticipantAction[]; compact?: boolean; className?: string }) {
  const voice = useVoice();
  const speaking = voice.speaking.has(p.socketId);
  const options = (
    <ActionsButton p={p} actions={actions} className="absolute right-1.5 top-1.5 opacity-0 focus:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100 [@media(hover:none)]:opacity-100" />
  );
  if (compact)
    return (
      <WithActions p={p} actions={actions}>
        <li
          className={cn(
            "group relative flex min-w-0 flex-col items-center gap-2 rounded-xl border bg-bg-subtle p-3 transition-[border-color,box-shadow]",
            speaking ? "border-success ring-2 ring-success" : "border-line",
            className,
          )}
        >
          <Avatar src={p.user.avatarUrl} name={p.user.displayName} size="lg" />
          <div className="flex max-w-full items-center gap-1.5 text-sm font-medium">
            <span className="truncate">{p.user.displayName}</span>
            {p.streaming && <LiveBadge />}
            <MuteIcon p={p} />
          </div>
          {options}
        </li>
      </WithActions>
    );
  // Full tile: same 16:9 frame as a stream, avatar centred, name in the corner.
  return (
    <WithActions p={p} actions={actions}>
      <li
        className={cn(
          "group relative flex aspect-[4/3] min-w-0 items-center justify-center rounded-xl border bg-bg-subtle transition-[border-color,box-shadow] md:aspect-video",
          speaking ? "border-success ring-2 ring-success" : "border-line",
          className,
        )}
      >
        <Avatar src={p.user.avatarUrl} name={p.user.displayName} size="xl" />
        <div className="absolute bottom-2 left-2 flex max-w-[calc(100%-1rem)] items-center gap-1.5 rounded-md bg-bg/80 px-2 py-1 text-sm font-medium backdrop-blur">
          <span className="truncate">{p.user.displayName}</span>
          {p.streaming && <LiveBadge />}
          <MuteIcon p={p} />
        </div>
        {options}
      </li>
    </WithActions>
  );
}

/**
 * Columns for n people: one on their own, then 2×2, 3×3, then four across.
 * Phones cap at two. Tiles wrap in a centred flex row, so a short last row
 * sits in the middle instead of hugging the left edge.
 */
function gridStyle(n: number) {
  const cols = n <= 1 ? 1 : n <= 4 ? 2 : n <= 9 ? 3 : 4;
  // Each tile is 1/cols of the row, less its share of the 0.75rem gaps.
  const width = (c: number) => `calc((100% - ${(c - 1) * 0.75}rem) / ${c})`;
  return { "--tile": width(cols), "--tile-sm": width(Math.min(cols, 2)) } as CSSProperties;
}

const TILE_WIDTH = "w-[var(--tile-sm)] md:w-[var(--tile)]";

/**
 * Everyone in a voice room, plus a tile for each screen share. Watching a
 * stream focuses it (big, with everyone else in a strip below); it can also
 * go full screen or picture in picture. While a room view is on screen the
 * floating mini player stays out of the way.
 */
export function RoomGrid({ participants, actions, compact }: { participants: VoiceParticipant[]; actions?: (p: VoiceParticipant) => ParticipantAction[]; compact?: boolean }) {
  const focusId = useStreamUi((s) => s.focused);
  const addView = useStreamUi((s) => s.addView);
  const roomId = participants[0]?.channelId;
  useEffect(() => (roomId ? addView(roomId) : undefined), [roomId, addView]);
  const streams = participants.filter((p) => p.streaming);
  const focused = streams.find((p) => p.socketId === focusId) ?? null;

  if (focused) {
    return (
      <div className="flex w-full flex-col gap-3">
        <StreamTile
          p={focused}
          participants={participants}
          focused
          className={cn("mx-auto w-full shrink-0", compact ? "max-w-[calc(40vh*16/9)]" : "max-w-[calc((100dvh-16rem)*16/9)]")}
        />
        <ul className="mx-auto flex w-fit max-w-full shrink-0 gap-3 overflow-x-auto pb-1" aria-label="Others in the room">
          {streams
            .filter((s) => s !== focused)
            .map((s) => (
              <li key={s.socketId} className="w-48 shrink-0">
                <StreamTile p={s} participants={participants} focused={false} small />
              </li>
            ))}
          {participants.map((p) => (
            <PersonTile key={p.socketId} p={p} actions={actions?.(p) ?? []} compact className="w-28 shrink-0" />
          ))}
        </ul>
      </div>
    );
  }
  return (
    <div className="flex w-full flex-col gap-3">
      {streams.length > 0 && (
        <div className={cn("grid gap-3", streams.length > 1 && "md:grid-cols-2", streams.length > 4 && "2xl:grid-cols-3")}>
          {streams.map((s) => (
            <StreamTile key={s.socketId} p={s} participants={participants} focused={false} />
          ))}
        </div>
      )}
      {compact ? (
        <ul className="flex w-full flex-wrap justify-center gap-3">
          {participants.map((p) => (
            <PersonTile key={p.socketId} p={p} actions={actions?.(p) ?? []} compact className="w-[140px]" />
          ))}
        </ul>
      ) : (
        <ul
          style={gridStyle(participants.length)}
          className={cn(
            "mx-auto flex w-full flex-wrap justify-center gap-3",
            participants.length === 1 ? "max-w-xl" : "max-w-5xl",
          )}
        >
          {participants.map((p) => (
            <PersonTile key={p.socketId} p={p} actions={actions?.(p) ?? []} className={TILE_WIDTH} />
          ))}
        </ul>
      )}
    </div>
  );
}
